import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Button,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import config from '../../../config';
import { AppContext } from '../../../modules/AppContext';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
  content: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  textField: { width: '220px', marginRight: '20px' },
  formRow: { display: 'flex', alignItems: 'center', marginTop: theme.spacing(2) },
  results: { marginTop: theme.spacing(3) },
  actions: { display: 'flex' },
}));

const parseOptionalId = (value, label) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return { id: null };
  if (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0) {
    return { error: `${label} must be a positive number.` };
  }
  return { id: trimmed };
};

const readErrorMessage = async (response) => {
  try {
    const text = (await response.text())?.trim();
    if (!text) return 'Unable to load screenshots.';
    if (text.startsWith('"')) return JSON.parse(text);
    return text;
  } catch (err) {
    return 'Unable to load screenshots.';
  }
};

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

export const FC3Screenshots = () => {
  const classes = useStyles();
  const { showToast } = useContext(AppContext);
  const [participantId, setParticipantId] = useState('');
  const [planSponsorId, setPlanSponsorId] = useState('');
  const [loading, setLoading] = useState(false);
  const [screenshots, setScreenshots] = useState(null);

  const handleClear = () => {
    setParticipantId('');
    setPlanSponsorId('');
    setScreenshots(null);
  };

  const handleSearch = async () => {
    const participant = parseOptionalId(participantId, 'Participant ID');
    const planSponsor = parseOptionalId(planSponsorId, 'Plan Sponsor ID');
    const validationError = participant.error || planSponsor.error;

    if (validationError) {
      showToast({ message: validationError, type: 'error' });
      return;
    }
    if (!participant.id && !planSponsor.id) {
      showToast({
        message: 'Enter a participant ID or a plan sponsor ID.',
        type: 'error',
      });
      return;
    }

    const url = new URL(`${config.pronvest.api.url}ParticipantScreenshots`);
    if (participant.id) url.searchParams.set('participantId', participant.id);
    if (planSponsor.id) url.searchParams.set('planSponsorId', planSponsor.id);

    setLoading(true);
    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `ApiKey ${config.pronvest.api.apiKey}`,
        },
      });

      if (!response.ok) {
        showToast({ message: await readErrorMessage(response), type: 'error' });
        setScreenshots(null);
        return;
      }

      setScreenshots((await response.json()) || []);
    } catch (err) {
      showToast({ message: 'Unable to load screenshots.', type: 'error' });
      setScreenshots(null);
    } finally {
      setLoading(false);
    }
  };

  const palmsUrl = config.pronvest.palms.url;

  return (
    <div className={classes.wrapper}>
      <Paper elevation={1} className={classes.content}>
        <Typography variant="h6">FC3 Session Screenshots</Typography>
        <section className={classes.formRow}>
          <TextField
            label="Participant ID"
            variant="outlined"
            className={classes.textField}
            value={participantId}
            onChange={(event) => setParticipantId(event.target.value)}
          />
          <TextField
            label="Plan Sponsor ID"
            variant="outlined"
            className={classes.textField}
            value={planSponsorId}
            onChange={(event) => setPlanSponsorId(event.target.value)}
          />
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleClear}
              style={{ marginLeft: 12 }}
            >
              Clear
            </Button>
          </div>
        </section>
        <section className={classes.results}>
          {loading && <CircularProgress />}
          {screenshots && (
            <Typography variant="body2" gutterBottom>
              {screenshots.length === 1
                ? '1 screenshot.'
                : `${screenshots.length} screenshots.`}
            </Typography>
          )}
          {screenshots && screenshots.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Participant ID</TableCell>
                  <TableCell>Participant</TableCell>
                  <TableCell>Plan Sponsor</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Screenshot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screenshots.map((screenshot) => (
                  <TableRow key={`${screenshot.participantId}-${screenshot.fileName}`}>
                    <TableCell>
                      <a
                        href={`${palmsUrl}participants/participant.aspx?id=${screenshot.participantId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {screenshot.participantId}
                      </a>
                    </TableCell>
                    <TableCell>{screenshot.participantName}</TableCell>
                    <TableCell>
                      {screenshot.planSponsorId ? (
                        <a
                          href={`${palmsUrl}plansponsors/plansponsor.aspx?id=${screenshot.planSponsorId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {screenshot.planSponsorName || screenshot.planSponsorId}
                        </a>
                      ) : (
                        screenshot.planSponsorName
                      )}
                    </TableCell>
                    <TableCell>{screenshot.screenshotType}</TableCell>
                    <TableCell>{formatDate(screenshot.screenshotDate)}</TableCell>
                    <TableCell>
                      <a
                        href={screenshot.screenshotUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {screenshot.fileName || 'Open'}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </Paper>
    </div>
  );
};
